// Text Input with character limit and error message

// Textarea with character limit and error message

// TODO DT-2802 Text Input

import {
	AerInputStructure,
	type InputProps,
} from "@aerstudios/aerated-aerinputstructure";
import { Filter } from "bad-words";
import cx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNationalDogSurveyContext } from "../../../NationalDogSurvey/NationalDogSurvey.context";
import Label from "../Label/Label";
import * as styles from "./TextInput.module.scss";

export interface TextInputProps extends InputProps {
	fieldType?: "text";
	// The name of the input
	name: string;
	// The ID of the text input
	id: string;
	// The label for the text input
	label: string;
	// The value of the text input
	value: string;
	// The function to call when the text is changed
	onChange: (value: string) => void;
	// The function to call when the text input errors
	handleError?: (fieldName: string, error: string) => void;
	// The maximum number of characters allowed
	maxLength?: number;

	// The minimum number of characters allowed
	minLength?: number;
	// The error message to display
	errorMessage?: string;
	// Should force validation check
	forceValidityCheck?: boolean;

	// If the input is disabled
	disabled?: boolean;

	// The pattern to match for validation
	pattern?: string;

	// If the input is required
	required?: boolean;
	// If the input is visually hidden
	hidden?: boolean;
	type?: "text" | "email" | "textarea";
	isLargeTextArea?: boolean;
	editableButton?: React.ReactNode;
	setDisableNext?: (value: boolean) => void;
	showCharacterCount?: boolean;
	applyProfanityFilter?: boolean;
}

export const NDSTextInput: React.FC<TextInputProps> = ({
	name,
	id,
	label,
	value,
	onChange,
	handleError,
	onBlur,
	minLength,
	maxLength,
	errorMessage,
	forceValidityCheck,
	disabled,
	pattern,
	required,
	hidden,
	type = "text",
	className,
	editableButton,
	fieldType: _,
	setDisableNext,
	showCharacterCount,
	isLargeTextArea = false,
	applyProfanityFilter = false,
	...rest
}) => {
	const [profanityErrorMessage, setProfanityErrorMessage] = useState<
		string | undefined
	>();
	const { questionSchema } = useNationalDogSurveyContext();
	const profanityList = questionSchema?.GLOBAL.profanityList;
	const allowedList = questionSchema?.GLOBAL.allowedList;

	const filter = useMemo(
		() =>
			new Filter({
				list: profanityList || [],
				exclude: allowedList,
			}),
		[profanityList, allowedList],
	);

	const handleChange = useCallback(
		(newValue: string) => {
			setProfanityErrorMessage(undefined);
			onChange(newValue);
		},
		[onChange],
	);

	// TODO - Need to update Aerated Memo to include blur in deps
	const handleBlur = useCallback(() => {
		if (applyProfanityFilter) {
			if (filter.isProfane(value)) {
				setProfanityErrorMessage("Please avoid using profanity");
				setDisableNext?.(true);
			} else {
				setProfanityErrorMessage(undefined);
				setDisableNext?.(false);
			}
		}
		onBlur?.(value);
	}, [filter, value, setDisableNext, onBlur, applyProfanityFilter]);

	useEffect(() => {
		if (!setDisableNext) return;

		if (profanityErrorMessage || errorMessage) {
			setDisableNext(true);
			handleError?.(name, profanityErrorMessage || errorMessage);
			return;
		}
		setDisableNext(false);
	}, [profanityErrorMessage, errorMessage, setDisableNext, handleError, name]);

	return (
		<AerInputStructure.Root
			className={cx(
				isLargeTextArea
					? styles.textInputWrapperLargeTextArea
					: styles.textInputWrapper,
				className,
			)}
			handleChange={handleChange}
			handleBlur={handleBlur}
			onKeyDown={(e) => {
				if (type !== "textarea" && e.key === "Enter") {
					e.preventDefault();
				}
			}}
			forceValidityCheck={forceValidityCheck}
			value={value}
			errorMessage={profanityErrorMessage || errorMessage}
		>
			{!hidden && <Label id={id} label={label} required={required} />}
			{editableButton ? (
				<div className={styles.editableFieldWrapper}>
					<AerInputStructure.Input
						{...rest}
						id={id}
						type={type}
						aria-autocomplete="inline"
						aria-label={label}
						disabled={disabled}
						className={styles.textInput}
						minLength={minLength}
						maxLength={maxLength}
						pattern={pattern}
						required={required}
						hidden={hidden}
						name={name}
					>
						{type === "textarea" ? <textarea /> : <input type={type} />}
					</AerInputStructure.Input>
					{editableButton}
				</div>
			) : (
				<>
					<AerInputStructure.Input
						{...rest}
						id={id}
						type={type}
						aria-autocomplete="inline"
						aria-label={label}
						disabled={disabled}
						className={styles.textInput}
						minLength={minLength}
						maxLength={maxLength}
						pattern={pattern}
						required={required}
						hidden={hidden}
						name={name}
					>
						{type === "textarea" ? <textarea /> : <input type={type} />}
					</AerInputStructure.Input>
					{!hidden && showCharacterCount && (
						<AerInputStructure.CharacterCount
							className={styles.characterCount}
							maxCount={maxLength}
						/>
					)}
				</>
			)}
			{!hidden && (
				<AerInputStructure.ErrorMessage className={styles.errorMessage} />
			)}
		</AerInputStructure.Root>
	);
};
